import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSignUp, useClerk } from '@clerk/clerk-react';
import { acceptInvitation } from '../../services/adminService';
import Header from '../common/Header';
import Footer from '../common/Footer';

const VerifyInvitationSignup: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const { signUp, isLoaded: isSignUpLoaded } = useSignUp();
  const clerk = useClerk();

  // Get the invitation token from session storage that was saved during signup
  const invitationToken = sessionStorage.getItem('pendingInvitationToken');

  useEffect(() => {
    // Redirect if no signUp attempt is in progress
    if (isSignUpLoaded && !signUp?.status) {
      navigate('/login');
    }
  }, [isSignUpLoaded, signUp, navigate]);

  const handleVerifyEmail = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter a valid verification code');
      return;
    }

    if (!signUp) {
      setError('No sign up in progress');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Attempt to verify the email with Clerk
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code: verificationCode
      });

      // Handle verification result
      if (completeSignUp.status === 'complete') {
        setSuccess('Email verification successful!');
        
        // Make sure we have a user ID
        if (!completeSignUp.createdUserId) {
          throw new Error('No user ID was created during signup');
        }
        
        // Set the user session as active
        if (completeSignUp.createdSessionId) {
          await clerk.setActive({
            session: completeSignUp.createdSessionId
          });
        }

        // If we have an invitation token, accept it via the backend
        if (invitationToken) {
          try {
            await acceptInvitation(invitationToken, completeSignUp.createdUserId);
            setSuccess('Account created and invitation accepted successfully!');
          } catch (inviteError: any) {
            // Don't block the user flow if this fails, but record the error
            console.error('Error accepting invitation:', inviteError);
            // Still show success for the account creation but mention issue with invitation
            setSuccess('Account created successfully, but there was an issue with accepting your invitation. The admin has been notified.');
          }
        }

        // Clear the stored token
        sessionStorage.removeItem('pendingInvitationToken');

        // Redirect to dashboard after successful signup and verification
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (error: any) {
      setError(error.message || 'Error verifying your email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!signUp) {
      setError('No sign up in progress');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare a new verification email
      await signUp.prepareEmailAddressVerification({
        strategy: 'email_code'
      });
      
      setSuccess('A new verification code has been sent to your email');
      setVerificationCode(''); // Clear the field
    } catch (error: any) {
      setError(error.message || 'Error sending new verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerifyEmail();
    }
  };

  if (!isSignUpLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f4f6f8"
      }}
    >
      <Header />

      <Container maxWidth="sm" sx={{ py: 6, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom align="center">
            Verify Your Email
          </Typography>
          
          <Typography variant="body1" paragraph align="center" sx={{ mb: 3 }}>
            We've sent a verification code to your email address. Please enter the code below to complete your registration.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            margin="normal"
            placeholder="Enter 6-digit code"
            inputProps={{ maxLength: 6 }}
            onKeyDown={handleKeyPress}
            sx={{ mb: 3 }}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={handleResendCode}
              disabled={loading}
              sx={{ textDecoration: 'none' }}
            >
              Resend Code
            </Link>
            
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleVerifyEmail}
            disabled={loading || verificationCode.length < 6}
            sx={{
              backgroundColor: "#5D5FEF",
              color: "#fff",
              py: 1.5,
              '&:hover': {
                backgroundColor: "#4A4BD5",
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Email'}
          </Button>
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default VerifyInvitationSignup; 